import React from 'react';
import { Position, positionsEqual } from '../types';
import './Cell.css';

interface CellProps {
  letter: string;
  position: Position;
  isSelected: boolean;
  isFound: boolean;
  isUnfound: boolean;  // For "give up" - words user didn't find
  foundWordIndex?: number;
  unfoundWordIndex?: number;  // Index of the unfound word
  letterIndexInWord?: number; // Position of this letter in the word (0 = first)
  onMouseDown: (pos: Position) => void;
  onMouseEnter: (pos: Position) => void;
  onMouseUp: () => void;
}

// Color palette for found words (bright, celebratory)
const FOUND_COLORS = [
  '#FFD700', // Gold
  '#FF6B6B', // Coral
  '#4ECDC4', // Teal
  '#45B7D1', // Sky blue
  '#96CEB4', // Sage
  '#FFEAA7', // Light yellow
  '#DDA0DD', // Plum
  '#98D8C8', // Mint
  '#F7DC6F', // Pale gold
  '#BB8FCE', // Light purple
];

// Color palette for unfound words (darker, distinct from found)
const UNFOUND_COLORS = [
  '#8B0000', // Dark red
  '#006400', // Dark green
  '#00008B', // Dark blue
  '#8B008B', // Dark magenta
  '#FF8C00', // Dark orange
  '#2F4F4F', // Dark slate gray
  '#4B0082', // Indigo
  '#800000', // Maroon
  '#008080', // Teal dark
  '#9932CC', // Dark orchid
];

const Cell: React.FC<CellProps> = ({
  letter,
  position,
  isSelected,
  isFound,
  isUnfound,
  foundWordIndex,
  unfoundWordIndex,
  letterIndexInWord,
  onMouseDown,
  onMouseEnter,
  onMouseUp
}) => {
  const getBackgroundColor = () => {
    if (isUnfound && unfoundWordIndex !== undefined) {
      return UNFOUND_COLORS[unfoundWordIndex % UNFOUND_COLORS.length];
    }
    if (isFound && foundWordIndex !== undefined) {
      return FOUND_COLORS[foundWordIndex % FOUND_COLORS.length];
    }
    if (isSelected) {
      return '#87CEEB'; // Light sky blue for selection
    }
    return '#ffffff';
  };

  const getTextColor = () => {
    if (isUnfound) {
      return '#ffffff'; // White text for unfound words
    }
    return '#333333'; // Default dark text
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    onMouseDown(position);
  };

  // Show letter position number for unfound words
  const isFirstLetter = isUnfound && letterIndexInWord === 0;

  return (
    <div
      className={`cell ${isSelected ? 'selected' : ''} ${isFound ? 'found' : ''} ${isUnfound ? 'unfound' : ''} ${isFirstLetter ? 'first-letter' : ''}`}
      style={{ backgroundColor: getBackgroundColor(), color: getTextColor() }}
      data-row={position.row}
      data-col={position.col}
    >
      {/* Inner hit zone - smaller centered area for mouse detection */}
      <div
        className="cell-hit-zone"
        onMouseDown={handleMouseDown}
        onMouseEnter={() => onMouseEnter(position)}
        onMouseUp={onMouseUp}
      >
        {letter}
      </div>
      {isUnfound && letterIndexInWord !== undefined && (
        <span className="letter-index">{letterIndexInWord + 1}</span>
      )}
    </div>
  );
};

export default Cell;

// Helper to check if a position is in the selection
export function isPositionInSelection(pos: Position, selection: Position[]): boolean {
  return selection.some(p => positionsEqual(p, pos));
}

// Helper to find the word index for a found position
export function getFoundWordIndex(
  pos: Position,
  foundWordPaths: { word: string; path: Position[] }[]
): number | undefined {
  for (let i = 0; i < foundWordPaths.length; i++) {
    if (foundWordPaths[i].path.some(p => positionsEqual(p, pos))) {
      return i;
    }
  }
  return undefined;
}

// Helper to get unfound word info for a position
export function getUnfoundWordInfo(
  pos: Position,
  allWords: { word: string; path: Position[] }[],
  foundWords: string[]
): { wordIndex: number; letterIndex: number } | undefined {
  let unfoundIndex = 0;
  for (const placedWord of allWords) {
    if (!foundWords.includes(placedWord.word)) {
      const letterIndex = placedWord.path.findIndex(p => positionsEqual(p, pos));
      if (letterIndex !== -1) {
        return { wordIndex: unfoundIndex, letterIndex };
      }
      unfoundIndex++;
    }
  }
  return undefined;
}
